import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/** Prefix for keys in localStorage (shared across tabs). Legacy sessionStorage entries are migrated on read. */
const STORAGE_PREFIX = 'qrda:cooldown:';

@Injectable({ providedIn: 'root' })
export class ButtonCooldownService implements OnDestroy {
  private readonly memoryEndsAt = new Map<string, number>();
  private readonly changed$ = new Subject<string>();
  private readonly storageSub: Subscription =
    typeof window !== 'undefined'
      ? fromEvent<StorageEvent>(window, 'storage').subscribe((ev) => this.onStorageEvent(ev))
      : Subscription.EMPTY;

  ngOnDestroy(): void {
    this.storageSub.unsubscribe();
    this.changed$.complete();
  }

  
  /**
   * Removes every cooldown key from localStorage, sessionStorage (legacy), and memory, then notifies subscribers.
   * Call on logout so timers reset for the next user session.
   */
  clearAll(): void {
    const shortKeys = new Set<string>();
    for (const fullKey of this.memoryEndsAt.keys()) {
      shortKeys.add(fullKey.slice(STORAGE_PREFIX.length));
    }
    this.memoryEndsAt.clear();
    if (typeof window !== 'undefined') {
      this.collectKeysAndRemovePrefix(window.localStorage, shortKeys);
      this.collectKeysAndRemovePrefix(window.sessionStorage, shortKeys);
    }
    shortKeys.forEach((k) => this.changed$.next(k));
  }

  /**
   * Seconds remaining for this cooldown key.
   * When `performCleanup` is true (default), expired entries are removed and a change is notified — use from timers / click handler, not from template getters (avoids `ExpressionChanged` during change detection).
   */
  secondsLeft(cooldownKey: string, performCleanup = true): number {
    const fullKey = this.fullKey(cooldownKey);
    const endsAt = this.readEndsAt(fullKey);
    if (endsAt === null) {
      return 0;
    }
    if (endsAt <= Date.now()) {
      if (performCleanup) {
        this.removeKey(fullKey, cooldownKey);
      }
      return 0;
    }
    return Math.ceil((endsAt - Date.now()) / 1000);
  }

  /**
   * Milliseconds remaining for this cooldown key. Provides the sub-second precision the directive needs
   * to drive a smooth progress ring; cleanup defaults to false because this is called from animation frames.
   */
  msLeft(cooldownKey: string, performCleanup = false): number {
    const fullKey = this.fullKey(cooldownKey);
    const endsAt = this.readEndsAt(fullKey);
    if (endsAt === null) {
      return 0;
    }
    const remaining = endsAt - Date.now();
    if (remaining <= 0) {
      if (performCleanup) {
        this.removeKey(fullKey, cooldownKey);
      }
      return 0;
    }
    return remaining;
  }

  beginCooldown(cooldownKey: string, durationMs: number): void {
    const fullKey = this.fullKey(cooldownKey);
    const endsAt = Date.now() + durationMs;
    this.writeEndsAt(fullKey, endsAt);
    this.changed$.next(cooldownKey);
  }

  /**
   * Emits when this cooldown key may have changed (same tab write, expiry cleanup, clearAll, or other tab via `storage`).
   */
  cooldownChanged$(cooldownKey: string): Observable<void> {
    return this.changed$.pipe(
      filter((k) => k === cooldownKey),
      map(() => undefined),
    );
  }

  private fullKey(cooldownKey: string): string {
    return `${STORAGE_PREFIX}${cooldownKey}`;
  }

  private getLocalStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  }

  /**
   * Reads end time from localStorage, or migrates a legacy sessionStorage value into localStorage.
   */
  private readEndsAt(fullKey: string): number | null {
    if (this.memoryEndsAt.has(fullKey)) {
      return this.memoryEndsAt.get(fullKey) ?? null;
    }
    const local = this.getLocalStorage();
    if (local) {
      try {
        let raw = local.getItem(fullKey);
        if (raw === null && typeof window !== 'undefined') {
          try {
            const legacy = window.sessionStorage.getItem(fullKey);
            if (legacy !== null) {
              local.setItem(fullKey, legacy);
              window.sessionStorage.removeItem(fullKey);
              raw = legacy;
            }
          } catch {
            // ignore migration failure
          }
        }
        if (raw === null) {
          return null;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private writeEndsAt(fullKey: string, endsAt: number): void {
    const local = this.getLocalStorage();
    if (local) {
      try {
        local.setItem(fullKey, String(endsAt));
        try {
          if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(fullKey);
          }
        } catch {
          // ignore
        }
        this.memoryEndsAt.delete(fullKey);
        return;
      } catch {
        // fall through to memory
      }
    }
    this.memoryEndsAt.set(fullKey, endsAt);
  }

  private removeKey(fullKey: string, shortKey: string): void {
    this.memoryEndsAt.delete(fullKey);
    const local = this.getLocalStorage();
    if (local) {
      try {
        local.removeItem(fullKey);
      } catch {
        // ignore
      }
    }
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(fullKey);
      }
    } catch {
      // ignore
    }
    this.changed$.next(shortKey);
  }

  private collectKeysAndRemovePrefix(store: Storage, out: Set<string>): void {
    const keys: string[] = [];
    try {
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k?.startsWith(STORAGE_PREFIX)) {
          keys.push(k);
        }
      }
      for (const fullKey of keys) {
        out.add(fullKey.slice(STORAGE_PREFIX.length));
        try {
          store.removeItem(fullKey);
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
  }

  private onStorageEvent(ev: StorageEvent): void {
    if (!ev.key || !ev.key.startsWith(STORAGE_PREFIX)) {
      return;
    }
    const shortKey = ev.key.slice(STORAGE_PREFIX.length);
    this.changed$.next(shortKey);
  }
}
